import { useState, useEffect } from 'react';
import { fetchGet } from '@cmd/ui-utilities';
import { getPkceFetchUrl } from '../webComponentUtils';
import pkceAuthClient from './pkceAuthClient';
import { placeholderResponses } from './placeholderData';
/**
 * Returns placeholder JSON data instead of calling the (now-sunset) backing APIs.
 * The original API-calling implementation is preserved below as dead code for reference.
 */
const useGetsDataPkce = (resourceGets, params, partition, transformFns) => {
    const [state, setState] = useState({
        data: null,
        isLoading: true,
        error: [],
    });
    useEffect(() => {
        const errors = [];
        const combinedData = resourceGets.reduce((acc, { resource }, index) => {
            const placeholderResponse = (placeholderResponses[resource] ?? null);
            if (!placeholderResponse) {
                errors.push(`No placeholder data for resource: ${resource}`);
                return acc;
            }
            errors.push(null);
            const transformed = transformFns && transformFns[index]
                ? transformFns[index](placeholderResponse)
                : { [index]: placeholderResponse };
            return { ...acc, ...transformed };
        }, {});
        setState({
            data: combinedData,
            isLoading: false,
            error: errors,
        });
    }, [params.startDate, params.endDate, params.orgId, params.limit]);
    return state;
};
export default useGetsDataPkce;
// -----------------------------------------------------------------------------
// ORIGINAL IMPLEMENTATION (dead code, retained for reference)
// The backing leads-data-service and transactions-data-service APIs are sunset.
// Remove this block once the services are fully decommissioned.
// -----------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/naming-convention
const useGetsDataPkce_ORIGINAL = (resourceGets, params, partition, transformFns) => {
    const [state, setState] = useState({
        data: null,
        isLoading: true,
        error: [],
    });
    const { data, isLoading, error } = state;
    const { startDate, endDate, orgId, limit, } = params;
    useEffect(() => {
        const fetchData = async (token) => {
            setState((prev) => ({ ...prev, isLoading: true, error: [] }));
            try {
                const responses = await Promise.all(resourceGets.map(({ baseUrl, resource }) => (fetchGet(getPkceFetchUrl(baseUrl, partition), resource, params, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }))));
                const combinedData = responses.reduce((acc, response, index) => {
                    const transformed = transformFns && transformFns[index]
                        ? transformFns[index](response.data)
                        : { [index]: response.data };
                    return { ...acc, ...transformed };
                }, {});
                setState((prev) => ({
                    ...prev,
                    data: combinedData,
                    isLoading: false,
                    error: responses.map((response) => response.error || null),
                }));
            }
            catch (responseError) {
                setState((prev) => ({ ...prev, isLoading: false, error: responseError }));
            }
        };
        pkceAuthClient.authenticate(fetchData, (e) => {
            setState((prev) => ({
                ...prev,
                isLoading: false,
                error: [e.message],
            }));
        });
    }, [startDate, endDate, orgId, limit]);
    return { data, isLoading, error };
};
